/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import {
  AllInclusive,
  ArrowDownward
} from "@material-ui/icons";
import ReactTooltip from 'react-tooltip';
import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Input,
  Card,
  CardHeader,
  CardBody,
  Table
} from "reactstrap";
import Toastr from "toastr";
import UploadDropzone from "../components/UploadDropzone";
import { useSaveScreeningMutation } from '../hooks/useSaveScreeningMutation';
import { useQueryParams } from '../hooks/useQueryParams';
import { useGetScreenings } from '../hooks/useGetScreenings';
import CustomPagination from '../components/CustomPagination';
import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';
import { DateTime } from 'luxon';
import ShowTranslationResultModal from '../components/ShowTranslationResultModal';

const RESULTS_PER_PAGE = 10;
const ScreeningPage: React.FC = () => {
  const query = useQueryParams()
  const [name, setName] = useState<string>("");
  const [inputFile, setInputFile] = useState<File>();
  const [modalText, setModalText] = useState<string>("");
  const [inputModalOpen, setInputModalOpen] = useState(false);

  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  });

  const {
    isError,
    isLoading,
    data,
    refetch,
  } = useGetScreenings(pageTable, RESULTS_PER_PAGE)

  const {
    mutate: saveBulkExtract,
    data: saveScreeningResponse,
    isLoading: saveScreeningLoading,
    isError: saveScreeningError,
  } = useSaveScreeningMutation();

  useEffect(() => {
    if (saveScreeningResponse) {
      Toastr.success(`Screening started for execution id ${saveScreeningResponse.jobId}`);
      setInputFile(undefined);
    }
  }, [saveScreeningResponse])

  useEffect(() => {
    if (saveScreeningError) {
      Toastr.error("Something bad happened.");
    }
  }, [saveScreeningError]);

  useEffect(() => {
    if (isError) {
      Toastr.error("Something bad happened while fetching data.")
    }
  }, [isError]);

  useEffect(() => {
    refetch()
  }, [pageTable])

  const handleSubmit = async () => {

    if (!inputFile) {
      Toastr.error("Please upload a file");
      return;
    }

    if (!name) {
      Toastr.error("Please enter a query");
      return;
    }

    saveBulkExtract({ file: inputFile, query: name });
  };

  const handleDownloadSample = () => {
    window.open("/Results_Investors_Screening_for_Kids_Plaza_Strategics_input_file.xlsx");
  }

  const makeModelOpen = (text: string) => {
    setModalText(text);
    setInputModalOpen(true)
  }

  const closeTranslationResultModel = () => {
    setInputModalOpen(false);
    setModalText("");
  }

  return (
    <>
      <div
        className="header pb-6 pt-5 pt-md-8"
        style={{
          height: '500px',
          backgroundColor: '#2D315C',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundImage: 'url(/background-header-infinity.jpg)',
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container
          className="mb-2"
          style={{ display: 'flex', flexDirection: 'column' }}
          fluid
        >
          <Row>
            <Col>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    display: 'inline-block',
                    width: '75%',
                    marginBottom: '10px',
                  }}
                  title={`Ensure your Excel file adheres to the following format: \n -  Must include 'Name' and 'Description' columns\n -  File format: .xls, .xlsx, .csv\n\nTo get started easily, download a sample input file by \nclicking the blue down arrow button below.`}
                >
                  <UploadDropzone
                    isFileUploaded={!!isLoading}
                    setFile={setInputFile}
                    accept={[
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'text/csv',
                    ]}
                    icon="/file-types-excel.png"
                    title="Click/drag and drop Excel/CSV file."
                  />
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    width: '75%',
                    marginBottom: '10px',
                  }}
                >
                  <Input
                    placeholder="Query, i.e. Operator of a chain of supermarkets and an online retail platform intended for babies as well as pregnant and postpartum mothers. The company offers a variety of products which include milk, vitamins, milk storage bag, diapers, newborn clothes and foot covers, eating utensils as well as cosmetics and beauty products, enabling customers to meet their baby product needs."
                    type="textarea"
                    name="text"
                    id="exampleText"
                    rows={5}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>
              <div className="text-center">
                {saveScreeningLoading ? (
                  <svg
                    height="70px"
                    viewBox="0 0 187.3 93.7"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <path
                      stroke="rgb(215, 61, 50)"
                      id="outline"
                      fill="none"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                    <path
                      id="outline-bg"
                      opacity="0.05"
                      fill="none"
                      stroke="#fff"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                  </svg>
                ) : (
                  <>
                    <Button
                      color="primary"
                      style={{
                        borderRadius: '50%',
                        width: '35px',
                        height: '35px',
                        padding: '0',
                      }}
                      data-tip="Download Sample"
                      onClick={handleDownloadSample}
                    >
                      <ArrowDownward />
                    </Button>
                    <ReactTooltip place="top" type="dark" effect="float" />
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isLoading}
                      style={{
                        width: '130px',
                        fontSize: '20px',
                        background: '#FF5252',
                        border: '#FF5252',
                      }}
                      className="btn-sm "
                    >
                      <AllInclusive />
                    </Button>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
        {data && data.data.length && (
          <Container className="mt--2" fluid>
            <Row className="mt-5">
              <Col className="mb-5 mb-xl-0">
                <Card className="shadow">
                  <CardHeader className="border-0">
                    <h3>Past Screenings</h3>
                  </CardHeader>
                  <CardBody>
                    <Table
                      className="align-items-center table-flush"
                      responsive
                    >
                      <thead className="thead-light">
                        <tr>
                          <th>ID</th>
                          <th>Query</th>
                          <th>Input File</th>
                          <th>Output File</th>
                          <th>Status</th>
                          <th>Executed By</th>
                          <th>Executed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.data.map((record) => (
                          <tr key={record.id}>
                            <td>{record.id}</td>
                            <td>
                              <p
                                title={record.query}
                                className="text-show-small"
                                onClick={() => makeModelOpen(record.query)}
                              >
                                {record.query}
                              </p>
                            </td>
                            <td>
                              {/* TODO: have token appended in react query hook (data can be transformed using `select` option) */}
                              <a
                                href={`${
                                  record.inputfileUrl
                                }?token=${localStorage.getItem('token')}`}
                                download
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fas fa-download"></i>
                              </a>
                            </td>
                            <td>
                              {record.outputFileUrl ? (
                                <a
                                  href={`${
                                    record.outputFileUrl
                                  }?token=${localStorage.getItem('token')}`}
                                  download
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <i className="fas fa-download"></i>
                                </a>
                              ) : (
                                <>NA</>
                              )}
                            </td>
                            <td>{record.status}</td>
                            <td>{record.createdBy}</td>
                            <td>
                              {record.createdAt
                                ? toCustomDateTimeString(
                                    DateTime.fromISO(record.createdAt)
                                  )
                                : 'NA'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <nav
                          style={{ display: 'inline-block' }}
                          aria-label="..."
                        >
                          <CustomPagination
                            activePage={pageTable}
                            totalResults={data.total}
                            resultsPerPage={RESULTS_PER_PAGE}
                            onChange={setPageTable}
                            label="Table navigation"
                          />
                        </nav>
                      </tfoot>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        )}
        <ShowTranslationResultModal
          text={modalText}
          open={inputModalOpen}
          onClose={() => closeTranslationResultModel()}
        />
      </div>
    </>
  );
};

export default ScreeningPage;
